/**
 * Contribution Service - Handle contribution transactions
 */
import { Connection, PublicKey, Transaction, SystemProgram } from '@solana/web3.js';
import { Program, BN } from '@project-serum/anchor';
import { TOKEN_PROGRAM_ID } from '@solana/spl-token';
import { 
  getConnection, 
  getProgram, 
  findPDA, 
  solToLamports, 
  handleError,
  PROGRAM_ID,
  AUCTION_SEED,
  DAY_SEED,
  USER_SEED,
  AFFILIATE_SEED,
  SOL_VAULT_SEED
} from './baseService';
import auctionService from './auctionService';

class ContributionService {
  constructor() {
    this.connection = null;
    this.program = null;
    this.wallet = null;
    this.initialized = false;
    this.auctionPDA = null;
  }

  /**
   * Initialize the contribution service
   * @param {Connection} connection - Solana connection
   * @param {Object} wallet - Solana wallet
   * @param {PublicKey} auctionPDA - Auction PDA
   * @returns {boolean} - Whether initialization was successful
   */
  async initialize(connection, wallet, auctionPDA) {
    try {
      this.connection = connection;
      this.wallet = wallet;
      this.auctionPDA = auctionPDA;
      
      // Load the program
      this.program = await getProgram(connection, wallet);
      
      this.initialized = true;
      return true;
    } catch (error) {
      console.error('Failed to initialize contribution service:', error);
      return false;
    }
  }

  /**
   * Contribute SOL to the auction for a specific day
   * @param {number} day - Day to contribute to
   * @param {number} amount - Amount of SOL to contribute
   * @param {string} referrer - Optional referrer public key
   * @param {number} entryPrice - Current price at time of contribution
   * @returns {Promise<string>} - Transaction signature
   */
  async contribute(day, amount, referrer = null, entryPrice = null) {
    if (!this.initialized) {
      throw new Error('Contribution service not initialized');
    }

    try {
      // Convert SOL to lamports
      const lamports = solToLamports(amount);
      
      // Find the day allocation PDA
      const [dayPDA, dayBump] = await findPDA([
        Buffer.from(DAY_SEED),
        this.auctionPDA.toBuffer(),
        Buffer.from([day])
      ]);
      
      // Find the user contribution PDA
      const [userPDA, userBump] = await findPDA([
        Buffer.from(USER_SEED),
        this.auctionPDA.toBuffer(),
        this.wallet.publicKey.toBuffer()
      ]);
      
      // Find the SOL vault PDA
      const [solVaultPDA, solVaultBump] = await findPDA([
        Buffer.from(SOL_VAULT_SEED),
        this.auctionPDA.toBuffer()
      ]);
      
      // Prepare the accounts for the transaction
      const accounts = {
        user: this.wallet.publicKey,
        auction: this.auctionPDA,
        dayAllocation: dayPDA,
        userContribution: userPDA,
        solVault: solVaultPDA,
        systemProgram: SystemProgram.programId
      };
      
      // Add referrer if provided
      if (referrer) {
        const referrerPubkey = new PublicKey(referrer);
        
        // Find the affiliate stats PDA
        const [affiliatePDA, affiliateBump] = await findPDA([
          Buffer.from(AFFILIATE_SEED),
          this.auctionPDA.toBuffer(),
          referrerPubkey.toBuffer()
        ]);
        
        accounts.referrer = referrerPubkey;
        accounts.affiliateStats = affiliatePDA;
      } else {
        // If no referrer, use a dummy key (will be ignored by the program)
        accounts.referrer = SystemProgram.programId;
      }
      
      // Call the contribute instruction
      const tx = await this.program.methods
        .contribute(
          day,
          new BN(lamports),
          referrer ? new PublicKey(referrer) : null
        )
        .accounts(accounts)
        .rpc();
        
      // For simulation purposes, store the contribution with entry price in local storage
      if (this.wallet && this.wallet.publicKey) {
        const walletAddress = this.wallet.publicKey.toString();
        
        // Get existing contributions
        const storedContributions = localStorage.getItem(`contributions_${walletAddress}`);
        let contributions = storedContributions ? JSON.parse(storedContributions) : {};
        
        // Add new contribution with entry price
        const dayContributions = contributions[day] || [];
        dayContributions.push({
          amount,
          timestamp: Date.now(),
          txId: tx,
          entryPrice: entryPrice || 0.0136 // Store entry price with contribution
        });
        
        // Update contributions
        contributions[day] = dayContributions;
        
        // Save to local storage
        localStorage.setItem(`contributions_${walletAddress}`, JSON.stringify(contributions));
        console.log('Stored contribution with entry price:', entryPrice);
      }

      return tx;
    } catch (error) {
      throw new Error(handleError(error));
    }
  }

  /**
   * Reserve SOL across all remaining days (dollar cost averaging)
   * @param {number} amount - Amount of SOL to reserve
   * @param {string} referrer - Optional referrer public key
   * @returns {Promise<string>} - Transaction signature
   */
  async reserveAllDays(amount, referrer = null) {
    if (!this.initialized) {
      throw new Error('Contribution service not initialized');
    }

    try {
      // Convert SOL to lamports
      const lamports = solToLamports(amount);
      
      // Find the SOL vault PDA
      const [solVaultPDA, solVaultBump] = await findPDA([
        Buffer.from(SOL_VAULT_SEED),
        this.auctionPDA.toBuffer()
      ]);
      
      // Prepare the accounts for the transaction
      const accounts = {
        user: this.wallet.publicKey,
        auction: this.auctionPDA,
        solVault: solVaultPDA,
        systemProgram: SystemProgram.programId
      };
      
      // Add referrer if provided
      if (referrer) {
        accounts.referrer = new PublicKey(referrer);
      } else {
        // If no referrer, use a dummy key (will be ignored by the program)
        accounts.referrer = SystemProgram.programId;
      }
      
      // Call the reserve_all_days instruction
      const tx = await this.program.methods
        .reserveAllDays(
          new BN(lamports),
          referrer ? new PublicKey(referrer) : null
        )
        .accounts(accounts)
        .rpc();

      return tx;
    } catch (error) {
      throw new Error(handleError(error));
    }
  }

  /**
   * Claim JINU rewards after auction ends
   * @returns {Promise<string>} - Transaction signature
   */
  async claimRewards() {
    if (!this.initialized) {
      throw new Error('Contribution service not initialized');
    }

    try {
      // Find the user contribution PDA
      const [userPDA, userBump] = await findPDA([
        Buffer.from(USER_SEED),
        this.auctionPDA.toBuffer(),
        this.wallet.publicKey.toBuffer()
      ]);
      
      // Find the affiliate stats PDA (if user is an affiliate)
      const [affiliatePDA, affiliateBump] = await findPDA([
        Buffer.from(AFFILIATE_SEED),
        this.auctionPDA.toBuffer(),
        this.wallet.publicKey.toBuffer()
      ]);
      
      // Call the claim_rewards instruction
      const tx = await this.program.methods
        .claimRewards()
        .accounts({
          user: this.wallet.publicKey,
          auction: this.auctionPDA,
          userContribution: userPDA,
          affiliateStats: affiliatePDA,
          tokenProgram: TOKEN_PROGRAM_ID
        })
        .rpc();

      return tx;
    } catch (error) {
      throw new Error(handleError(error));
    }
  }

  /**
   * Get user contributions for the current wallet
   * @returns {Promise<Object>} - User contributions
   */
  async getUserContributions() {
    if (!this.initialized) {
      throw new Error('Contribution service not initialized');
    }

    try {
      // Find the user contribution PDA
      const [userPDA] = await findPDA([
        Buffer.from(USER_SEED),
        this.auctionPDA.toBuffer(),
        this.wallet.publicKey.toBuffer()
      ]);
      
      // Fetch user contribution data
      const userContribution = await this.program.account.userContribution.fetch(userPDA);
      
      // Format the data
      const contributions = {};
      for (const contrib of userContribution.dayContributions) {
        contributions[contrib.day] = {
          amount: contrib.amount,
          timestamp: new Date(contrib.timestamp * 1000)
        };
      }
      
      return {
        totalContributed: userContribution.totalContributed,
        contributions,
        referrer: userContribution.referrer,
        rewardsClaimed: userContribution.rewardsClaimed
      };
    } catch (error) {
      // User might not have contributed yet
      return {
        totalContributed: 0,
        contributions: {},
        referrer: null,
        rewardsClaimed: false
      };
    }
  }

  /**
   * Calculate user's projected JINU rewards
   * @returns {Promise<Object>} - Projected rewards
   */
  async calculateProjectedRewards() {
    if (!this.initialized) {
      throw new Error('Contribution service not initialized');
    }

    try {
      // Get auction state
      const auctionState = await auctionService.getAuctionState();
      
      // Get user contributions
      const userContributions = await this.getUserContributions();
      
      // Calculate user's share of the auction pool
      const userShare = auctionState.totalSolRaised > 0
        ? (userContributions.totalContributed / auctionState.totalSolRaised)
        : 0;
      
      // Calculate projected JINU rewards
      const projectedJinu = userShare * auctionState.auctionPool;
      
      // Calculate affiliate rewards if applicable
      let affiliateRewards = 0;
      try {
        // Find the affiliate stats PDA
        const [affiliatePDA, affiliateBump] = await findPDA([
          Buffer.from(AFFILIATE_SEED),
          this.auctionPDA.toBuffer(),
          this.wallet.publicKey.toBuffer()
        ]);
        
        // Fetch affiliate stats
        const affiliateStats = await this.program.account.affiliateStats.fetch(affiliatePDA);
        
        // Calculate affiliate rewards (simplified)
        affiliateRewards = affiliateStats.totalReferrals * (auctionState.affiliatePool / 100);
      } catch (error) {
        // User is not an affiliate or has no referrals
      }
      
      return {
        userShare,
        projectedJinu,
        affiliateRewards,
        totalRewards: projectedJinu + affiliateRewards
      };
    } catch (error) {
      throw new Error(handleError(error));
    }
  }

  /**
   * Get day statistics with projection
   * @param {number} day - Day number (1-50)
   * @param {number} projectedAmount - Projected contribution amount
   * @returns {Promise<Object>} - Day statistics with projection
   */
  async getDayStatsWithProjection(day, projectedAmount = 0) {
    if (!this.initialized) {
      throw new Error('Contribution service not initialized');
    }

    try {
      // Get day allocation
      const dayAllocation = await auctionService.getDayAllocation(day);
      
      // Get user contributions for this day
      const userContributions = await this.getUserContributions();
      const dayContribution = userContributions.contributions[day] || { amount: 0 };
      
      // Convert projected amount to lamports
      const projectedLamports = solToLamports(projectedAmount);
      
      // Calculate total contribution with projection
      const totalContribution = dayContribution.amount + projectedLamports;
      
      // Calculate user's share of the day
      const userShare = dayAllocation.totalContributions > 0
        ? (totalContribution / (dayAllocation.totalContributions + projectedLamports))
        : 1;
      
      // Calculate projected JINU for the day
      const projectedJinu = userShare * dayAllocation.jinuAllocation;
      
      return {
        day,
        totalContributions: dayAllocation.totalContributions,
        totalParticipants: dayAllocation.totalParticipants,
        jinuAllocation: dayAllocation.jinuAllocation,
        userContribution: dayContribution.amount,
        projectedContribution: projectedLamports,
        userShare,
        projectedJinu
      };
    } catch (error) {
      // Day might not be initialized yet
      return {
        day,
        totalContributions: 0,
        totalParticipants: 0,
        jinuAllocation: 0,
        userContribution: 0,
        projectedContribution: projectedAmount,
        userShare: 1,
        projectedJinu: 0
      };
    }
  }
}

// Create a singleton instance
const contributionService = new ContributionService();
export default contributionService;
